import type { Job } from '@mairistem/composants';
import type { Bordereau } from '@mairistem/comptabilite';
import type { TemplateRequestGet } from '@mairistem/finances-utils';
import { request } from '@mairistem/tools';
import type DuplicatePayload from '../../../interfaces/pieces/DuplicatePayload';
import type { BordereauWithState } from '../../../interfaces/pieces/TreeRow';
import type { InfoChorus } from '../../../utils/chorus/afficherInfoChorus';
import type { AvertissementApi } from '../../../utils/disponible';

export const createBordereau = (
	bordereau: Partial<Bordereau> | BordereauWithState,
) => {
	const url = '/api/finances/comptabilite/piecescomptables/bordereaux/';
	return request.post<Partial<Bordereau>, Bordereau>(url, bordereau);
};

export const updateBordereau = (
	bordereau: Partial<Bordereau>,
	params?: URLSearchParams,
) => {
	const url = '/api/finances/comptabilite/piecescomptables/bordereaux/';
	const query = params ? `?${params.toString()}` : '';
	return request.put<Partial<Bordereau>, Bordereau>(
		`${url}${bordereau.identifiant}${query}`,
		bordereau,
	);
};

/** Options de numérotation : date des pièces (gereDate, dateEmission) et ordre de numérotation. */
export interface NumerotationBordereau {
	gereDate?: number;
	ordre?: number;
	dateEmission?: string;
}

/**
 * Lance la numérotation d'un bordereau en brouillard (il passe en historisé avec ses pièces) : Job360 à suivre, qui
 * reçoit le bordereau numéroté ou le message de l'erreur.
 */
export const numeroterBordereau = (
	identifiantBordereau: number | string,
	numerotation: NumerotationBordereau,
) => {
	const url = `/api/finances/comptabilite/piecescomptables/bordereaux/${identifiantBordereau}/numeroter`;
	return request.put<NumerotationBordereau, Job>(url, numerotation);
};

/**
 * Lance la dénumérotation du dernier bordereau numéroté (il repasse en brouillard avec ses pièces) : Job360 à
 * suivre, qui reçoit le bordereau dénuméroté ou le message de l'erreur.
 */
export const denumeroterBordereau = (identifiantBordereau: number | string) => {
	const url = `/api/finances/comptabilite/piecescomptables/bordereaux/${identifiantBordereau}/denumeroter`;
	return request.put<unknown, Job>(url);
};

/**
 * Lance la duplication d'un bordereau : Job360 à suivre, avec les dépassements de disponible non bloquants
 * (warnings). Un dépassement bloquant refuse la duplication (erreur de l'API).
 */
export const dupliquerBordereau = (
	bordereau: Partial<Bordereau>,
	shouldReprendreReferences: boolean,
	dateEcheance?: string,
	dateDepartDelaiGlobalPaiement?: string,
	datePrelevement?: string,
	dateRealisationPrestationDebut?: string,
	dateRealisationPrestationFin?: string,
) => {
	const parameters: DuplicatePayload = {
		shouldReprendreReferences,
		dateEcheance,
		dateDepartDelaiGlobalPaiement,
		datePrelevement,
		dateRealisationPrestationDebut,
		dateRealisationPrestationFin,
	};

	const url = `/api/finances/comptabilite/piecescomptables/bordereaux/${bordereau.identifiant}/dupliquer`;
	return request.post<
		DuplicatePayload,
		Job & { warnings?: AvertissementApi[] }
	>(url, parameters);
};

export const getBordereau = (
	identifiantBordereau: number | string,
	params?: URLSearchParams,
) => {
	const url = '/api/finances/comptabilite/piecescomptables/bordereaux/';
	const query = params ? `?${params.toString()}` : '';
	return request.get<unknown, Bordereau>(
		`${url}${identifiantBordereau}${query}`,
	);
};

export const getBordereaux = (params: URLSearchParams) => {
	const url = '/api/finances/comptabilite/piecescomptables/bordereaux?';
	return request.get<unknown, TemplateRequestGet<Bordereau>>(
		`${url}${params.toString()}`,
	);
};

/**
 * Supprime un bordereau : mandats issus de factures Chorus concernés en retour (InfoChorus). L'API répond 200 avec
 * ce corps : request.custom, car request.delete n'accepte que 204 ; il résout la réponse entière.
 */
export const deleteBordereau = async (
	identifiantBordereau: number,
): Promise<InfoChorus> => {
	const url = '/api/finances/comptabilite/piecescomptables/bordereaux/';
	const reponse = await request.custom<unknown, { data?: InfoChorus }>(
		'delete',
		`${url}${identifiantBordereau}`,
	);
	return reponse?.data || {};
};

export const updateBordereauEtat = (
	identifiantBordereau: number,
	params?: URLSearchParams,
) => {
	const url = `/api/finances/comptabilite/piecescomptables/bordereaux/${identifiantBordereau}/majStatusDossier`;
	const query = params ? `?${params.toString()}` : '';
	return request.get<unknown, Bordereau>(`${url}${query}`);
};

/**
 * Options du rejet ou de l'annulation d'un bordereau entier (API : lancerRejetBordereau), comme pour une pièce :
 * ré-engagement des montants, et pièces de rattachement des charges et produits (nature 10).
 */
export interface OptionsRejetBordereau {
	reengager?: boolean;
	isRattachChargeProduit?: boolean;
}

export const rejeterBordereau = (
	identifiantBordereau: number,
	options: OptionsRejetBordereau = {},
) => {
	const url = `/api/finances/comptabilite/piecescomptables/bordereaux/${identifiantBordereau}/rejeter`;
	return request.post<OptionsRejetBordereau, Job>(url, options);
};

/** Même traitement que le rejet, vers un bordereau d'annulation (02) créé par l'API. */
export const annulerBordereau = (
	identifiantBordereau: number,
	options: OptionsRejetBordereau = {},
) => {
	const url = `/api/finances/comptabilite/piecescomptables/bordereaux/${identifiantBordereau}/annuler`;
	return request.post<OptionsRejetBordereau, Job>(url, options);
};
